import "./AccountStatusItemList.scss"

import {
  AccountStatusItemList as SLAccountStatusItemList,
  Checkbox,
  FooterVariant,
  InlineNotification,
  InlineNotificationVariant,
  Loader,
  MenuItem,
  Modal,
  ModalVariant,
  PromptVariant,
  SvgIcon,
} from "@skylead/component-library"
import classNames from "classnames"
import countries from "i18n-iso-countries"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import {
  cancelSubscription,
  changeAccountName,
  changeTimezone,
  clearForm,
  clearFormErrors,
  closeModal,
  deleteSeat,
  getAllCancellationReasons,
  getProxyCountries,
  getTimezone,
  setActiveAccountID,
  setFormErrors,
  showModal,
  showPrompt,
  updateFormField,
} from "../../../redux/actions"
import { store } from "../../../redux/store"
import { AccountStatusItemListModals } from "../../../utils/account-status-item-list-hook"
import {
  choosePerson,
  isAccountActive,
  onLabelClick,
  openEmailProviderConnect,
  reactivateAccountModal,
} from "../../../utils/account-utils"
import { useCustomMenu, useGlobalTranslation } from "../../../utils/hook"
import {
  getFirstEligibleAccountUrl,
  hasPermission,
  PERMISSIONS,
} from "../../../utils/team-management/team-management-utils"
import { validateChangeAccountName } from "../../../utils/validation/validation-utils"
import LangText from "../../atoms/LangText"
import InputGroup from "../InputGroup"

countries.registerLocale(require("i18n-iso-countries/langs/en.json"))

const AccountStatusItemList = ({
  loading,
  loadingSearchingSeats,
  small = false,
  filter = "",
  pricingPlans = [],
  accountCount = 0,
  isHeaderMenu,
  isLandingPage = false,
  setTooltip,
  closeTooltip,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { tt } = useGlobalTranslation()

  const {
    availableSeats,
    user,
    teamAccounts,
    accountStatuses,
    proxyCountries,
    accountID,
    isShownInfoModal,
    userID,
    savedCard,
    accountProxy,
    accountSpecificPrices,
    allCancellationReasons,
    selectedTeam,
    isEuropeanFormat,
  } = useSelector(reduxState => ({
    availableSeats: reduxState.payment.availableSeats,
    user: reduxState.user.profile,
    teamAccounts: reduxState.teamManagement.teamAccounts.items,
    accountStatuses: reduxState.app.accountStatuses,
    proxyCountries: reduxState.app.proxyCountries,
    accountID: reduxState.account.activeAccountID,
    isShownInfoModal: reduxState.app.infoModalData.show,
    userID: reduxState.user.profile.id,
    savedCard: reduxState.payment.savedCard,
    accountProxy: reduxState.account.proxyInfo,
    accountSpecificPrices: reduxState.payment.accountSpecificPrices,
    allCancellationReasons: reduxState.app.cancellationReasons,
    selectedTeam: reduxState.teamManagement.selectedTeam,
    isEuropeanFormat: reduxState.app.isEuropeanFormat,
  }))

  const [cancellationReasons, setCancellationReasons] = useState([])
  const [searchedAccounts, setSearchedAccounts] = useState(teamAccounts)
  const [customMenu, setCustomMenu] = useCustomMenu("custom-menu")
  const [modalId, setModalId] = useState()

  const initialState = {
    selectedAccount: {
      accountGlobalStatusId: 0,
    },
    showPaymentNeededModal: false,
    amountForPaymentNeeded: 0,
    showAccountSettings: false,
    showSavedCard: false,
    showDefaultProxies: true,
    cancelSubscriptionModal: false,
    deleteAccountModal: false,
  }

  const [state, changeState] = useState({ ...initialState })

  const {
    selectedAccount,
    showAccountSettings,
    deleteAccountModal,
    showPaymentNeededModal,
    cancelSubscriptionModal,
  } = state

  const setState = newState => changeState(prevState => ({ ...prevState, ...newState }))

  const isCancelSubscription = useMemo(() => {
    return modalId === "cancelSubscriptionModal"
  }, [modalId])

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getAllCancellationReasons())
        setState({ loading: false })
      } catch {
        setState({ loading: false })
      }
    }
    const controller = new AbortController()
    loadData()
    dispatch(getProxyCountries())

    return () => {
      controller.abort()
      setState({ ...initialState })
    }
  }, [])

  useEffect(() => {
    setSearchedAccounts(teamAccounts.filter(acc => acc.fullName.toLowerCase().includes(filter)))
  }, [teamAccounts])

  const accountStatusItemListClass = classNames("account-status-item-list-wrapper")

  const hideModal = () => {
    setModalId(undefined)
    setCancellationReasons([])
    dispatch(clearForm())
  }

  const handleSubmitAccountNameChange = async event => {
    event.preventDefault()
    event.stopPropagation()
    const { newAccountName } = store.getState().forms.formData

    dispatch(clearFormErrors())
    const allErrors = validateChangeAccountName({ newAccountName })
    dispatch(setFormErrors(allErrors))

    if (allErrors) return false

    dispatch(changeAccountName(tt, newAccountName))
    setState({ showAccountSettings: false })
  }

  const showChangeAccountNameModal = person => {
    dispatch(
      showModal(
        ModalVariant.SMALL,
        tt("rename-seat"),
        <InputGroup
          field="newAccountName"
          label={tt("seat-name")}
          type="text"
          placeholder={tt("seat-name")}
          inputGroupClassName="input-on-surface"
          customValue={person.fullName}
        />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("rename"), onClick: handleSubmitAccountNameChange },
        { label: tt("cancel"), onClick: () => dispatch(closeModal()) },
      ),
    )
  }

  const handleSubmitAccountTimeZoneChange = async () => {
    const { selectedTimezone } = store.getState().forms.formData

    await dispatch(
      changeTimezone(tt, {
        timeZone: selectedTimezone.name,
        startUTCTime: "09:00",
        endUTCTime: "17:00",
      }),
    )
    setState({ showAccountSettings: false })
  }

  const showChangeSeatTimeZone = async () => {
    await dispatch(getTimezone())
    dispatch(
      showModal(
        ModalVariant.SMALL,
        tt("change-time-zone"),
        <div className="wrapper-timezone">
          <InputGroup
            field="selectedTimezone"
            type="timezone"
            label={tt("time-zone")}
            inputGroupClassName="input-on-surface"
            inputClassName="timezone-picker-seat-settings"
            inputProps={{
              placeholder: tt("select-timezone-placeholder"),
              name: "timezone",
            }}
            message={tt("account.statement.list.change-seat-timezone.modal.msg")}
          />
        </div>,
        FooterVariant.TRANSACTIONAL,
        { label: tt("save"), onClick: handleSubmitAccountTimeZoneChange },
        { label: tt("cancel") },
      ),
    )
  }

  const getTranslatedCancelReason = item => {
    switch (item) {
      case "The product is too expensive":
        return tt("cancel-reason.product-too-expensive")
      case "Support was not meeting my expectations":
        return tt("cancel-reason.support-not-meeting-expectation")
      case "The product is missing features that I need":
        return tt("cancel-reason.feature-missing")
      case "Not sure how to use existing features & tools":
        return tt("cancel-reason.how-to-use-feature")
      case "I didn’t get new business using cold outreach":
        return tt("cancel-reason.not-get-new-business")
      case "Switching to another tool/product":
        return tt("cancel-reason.switch-to-another-tool")
      case "I had many issues with platform":
        return tt("cancel-reason.too-many-issues")
      case "The resolution time of my issue was slower than expected":
        return tt("cancel-reason.resolution-time")
      case "Other":
        return tt("cancel-reason.other")
      default:
        return item
    }
  }

  const renderCancellationReasons = () => (
    <div className="account-cancellation-reasons">
      {allCancellationReasons.items.map(reason => {
        const isReasonChecked = cancellationReasons.find(
          cancellationReason => cancellationReason.id === reason.id,
        )
        return (
          <div key={reason.id} className="cancellation-reasons">
            <div className="reason">
              <Checkbox
                id={`step-${reason.id}`}
                checked={isReasonChecked}
                onClick={() => {
                  if (isReasonChecked) {
                    setCancellationReasons(
                      cancellationReasons.filter(
                        cancellationReason => cancellationReason.id !== reason.id,
                      ),
                    )
                  } else {
                    setCancellationReasons([...cancellationReasons, reason])
                  }
                }}
              />
              <span className="large" htmlFor={`step-${reason.id}`}>
                {getTranslatedCancelReason(reason.name)}
              </span>
            </div>
            {reason.commentRequired && isReasonChecked && (
              <InputGroup
                bordered
                field={reason.id}
                placeholder={tt("tell-us-more-placeholder")}
                type="textarea"
                useEnterAsDefault
                inputProps={{ rows: 3 }}
              />
            )}
          </div>
        )
      })}
    </div>
  )

  const handleReasonsSubmit = async () => {
    dispatch(updateFormField("loadingButton", true))
    const { formData } = store.getState().forms
    let cancellationReasonsValid = true
    if (cancellationReasons.length === 0) {
      cancellationReasonsValid = false
      dispatch(
        showPrompt(
          PromptVariant.INFO,
          tt("warning"),
          tt("account.statement.list.reason-submit.no-input.prompt.msg"),
        ),
      )
    } else {
      cancellationReasons.forEach(cancellationReason => {
        if (cancellationReason.commentRequired && !formData[cancellationReason.id]) {
          cancellationReasonsValid = false
          dispatch(
            setFormErrors({
              [cancellationReason.id]: tt("account.statement.list.cancel.reason.error"),
            }),
          )
        }
      })
    }
    if (cancellationReasonsValid) {
      const reason = cancellationReasons
        .map(cancellationReason => {
          if (cancellationReason.commentRequired) {
            return `${cancellationReason.name}: ${formData[cancellationReason.id]}`
          }
          return cancellationReason.name
        })
        .join("\n")
      const callback = () => {
        setCancellationReasons([])
      }
      await dispatch(
        isCancelSubscription
          ? cancelSubscription(tt, reason, callback)
          : deleteSeat(tt, reason, callback),
      )
      hideModal()
    }
    dispatch(updateFormField("loadingButton", false))
  }

  const onSeatClick = (event, person) => {
    event.stopPropagation()
    const accountId = person.id
    dispatch(setActiveAccountID(accountId))

    setCustomMenu(
      event,
      <div className="seat-custom-menu">
        {person.email && (
          <>
            <div className="seat-info">
              <span className="large">
                <LangText langKey="account-info" />
              </span>
              <ul className="small">
                <li>
                  <LangText langKey="label.email" />{" "}
                  <span className="seat-info__email">{person.email}</span>
                </li>
                <li>{`${tt("label.proxy-location")} ${
                  accountProxy
                    ? accountProxy.proxyCountryCode
                      ? countries.getName(accountProxy.proxyCountryCode.toUpperCase(), "en")
                      : tt("custom")
                    : tt("none")
                }`}</li>
                <li>
                  {`${tt("label.next-renewal-date")} ${
                    person?.subscription?.subscriptionEndsAt
                      ? moment.unix(person?.subscription?.subscriptionEndsAt).format("MM.DD.YYYY")
                      : "-"
                  }`}
                </li>
              </ul>
            </div>
            <SvgIcon icon="seat-menu-divider" className="seat-divider" />
          </>
        )}
        {hasPermission(PERMISSIONS.SEAT_SETTINGS_EDIT, accountId) && (
          <MenuItem
            icon="pencil-20"
            label={tt("rename-seat")}
            onClickContainer={() => showChangeAccountNameModal(person)}
          />
        )}
        {hasPermission(PERMISSIONS.SEAT_SETTINGS_EDIT, accountId) && (
          <MenuItem
            icon="clock-pin-20"
            label={tt("change-time-zone")}
            onClickContainer={showChangeSeatTimeZone}
          />
        )}
        {hasPermission(PERMISSIONS.CANCEL_SUBSCRIPTION_EDIT, accountId) && !person.cancelAt && (
          <MenuItem
            icon="circle-x-20"
            label={tt("cancel-subscription")}
            onClickContainer={() => {
              setModalId("cancelSubscriptionModal")
            }}
          />
        )}

        {person.accountGlobalStatusId === 8 &&
          hasPermission(PERMISSIONS.LINKEDIN_INTEGRATIONS_EDIT, accountId) && (
            <>
              <MenuItem
                icon="plus-20"
                label={tt("reactivate-subscription")}
                onClickContainer={() => {
                  reactivateAccountModal(tt, person)
                }}
              />
            </>
          )}

        {hasPermission(PERMISSIONS.DELETE_ACCOUNT_EDIT, accountId) && (
          <MenuItem
            icon="trash-20"
            label={tt("delete-seat")}
            customClass="delete-menu-item"
            onClickContainer={() => {
              setModalId("deleteAccountModal")
            }}
          />
        )}
      </div>,
      40,
      48,
    )
  }

  const onAccountItemClick = async (e, person, openInNewTab) => {
    const changeAccount = async () => {
      await choosePerson(
        person,
        true,
        { showAccountSettings: false },
        small,
        userID,
        selectedTeam,
        setState,
        true,
      )
      if (
        isAccountActive(
          person.accountGlobalStatusId,
          person.accountPaymentStatusId,
          person.suspended,
        )
      ) {
        /**
         * In case user is accessing the list from the MasterLogin - it has to be calculated
         * which inner account page is the 'first' they have permission for
         * */
        const firstEligibleAccountUrl = getFirstEligibleAccountUrl(user, selectedTeam, person)
        if (firstEligibleAccountUrl) {
          if (openInNewTab) {
            window.open(firstEligibleAccountUrl, "_blank")
          } else {
            navigate(firstEligibleAccountUrl)
          }
        }
      } else {
        await onLabelClick(
          tt,
          undefined,
          person,
          accountStatuses,
          small,
          userID,
          setState,
          proxyCountries,
          undefined,
          undefined,
          navigate,
        )
      }
    }
    e.stopPropagation()
    if (!small || (small && accountID !== person.id)) {
      await changeAccount()
    }
  }

  if (loading) return <Loader fullWidth margin="20px 0px 0px 0px" />

  const renderAccountStatusModals = () => {
    return (
      <>
        {!isShownInfoModal &&
          (showAccountSettings ||
            deleteAccountModal ||
            showPaymentNeededModal ||
            cancelSubscriptionModal) && (
            <>
              {AccountStatusItemListModals(
                tt,
                user,
                selectedAccount,
                accountProxy,
                countries,
                savedCard,
                setState,
                accountSpecificPrices,
                pricingPlans,
                availableSeats,
                allCancellationReasons,
                setCancellationReasons,
                cancellationReasons,
                selectedTeam?.canManagePayment,
                state,
              )}
            </>
          )}
      </>
    )
  }
  return (
    <>
      {customMenu}
      <SLAccountStatusItemList
        isLandingPage={isLandingPage}
        loadingSearchingSeats={loadingSearchingSeats}
        accountCount={accountCount}
        searchedAccounts={searchedAccounts}
        teamAccounts={teamAccounts}
        onSeatClick={onSeatClick}
        onAccountItemClick={onAccountItemClick}
        small={small}
        setState={setState}
        hasPermission={hasPermission}
        accountStatuses={accountStatuses}
        onLabelClick={onLabelClick}
        accountStatusItemListClass={accountStatusItemListClass}
        renderAccountStatusModals={renderAccountStatusModals}
        userID={userID}
        selectedAccount={selectedAccount}
        proxyCountries={proxyCountries}
        isShownInfoModal={isShownInfoModal}
        selectedTeam={selectedTeam}
        isHeaderMenu={isHeaderMenu}
        setTooltip={setTooltip}
        closeTooltip={closeTooltip}
        openEmailProviderConnect={openEmailProviderConnect}
        user={user}
        accountID={accountID}
        setCustomMenu={setCustomMenu}
        accountProxy={accountProxy}
        countries={countries}
        setModalId={setModalId}
        showChangeSeatTimeZone={showChangeSeatTimeZone}
        tt={tt}
      />

      {modalId && (
        <Modal
          show={modalId !== undefined}
          type={ModalVariant.SMALL}
          title={isCancelSubscription ? tt("cancel-subscription") : tt("delete-seat")}
          content={
            <div id="cancel-subscription">
              <div className="messages">
                <span className="large-bold">
                  <LangText langKey="account.statement.list.reason.msg.1" />
                </span>
                <span className="large">
                  <LangText langKey="account.statement.list.reason.msg.2" />
                </span>
              </div>
              {renderCancellationReasons()}
              {selectedAccount.subscription && (
                <InlineNotification
                  type={InlineNotificationVariant.WARNING}
                  label={
                    isCancelSubscription
                      ? `${"account.statement.list.is-cancel-subscription.msg.1"}\n \n${"account.statement.list.is-cancel-subscription.msg.2"} ${moment
                          .unix(selectedAccount.subscription.subscriptionEndsAt)
                          .format(isEuropeanFormat ? "DD/MM/YYYY" : "MM/DD/YYYY")}`
                      : tt("account.statement.list.is-not-cancel-subscription.msg")
                  }
                />
              )}
            </div>
          }
          footerVariant={FooterVariant.TRANSACTIONAL_DESTRUCTIVE}
          primaryBtn={{
            label: isCancelSubscription ? tt("cancel-subscription") : tt("delete"),
            onClick: handleReasonsSubmit,
          }}
          secondaryBtn={{ label: tt("i-changed-my-mind"), onClick: hideModal }}
        />
      )}
    </>
  )
}

// AccountStatusItemList.propTypes = {
//   loading: PropTypes.bool.isRequired,
//   loadingSearchingSeats: PropTypes.bool.isRequired,
//   accountCount: PropTypes.number,
//   pricingPlans: PropTypes.instanceOf(Array),
//   small: PropTypes.bool,
//   filter: PropTypes.string,
//   isLandingPage: PropTypes.bool,
// }

export default React.memo(AccountStatusItemList)
